/**
 * Dashboard Page Component
 * Main dashboard layout with table
 */

import { useMemo } from 'react';
import { LandingTopWidget } from '../../organisms/landing';
import { LayoutSidebar } from '../../organisms/sidebar/LayoutSidebar';
import { TableProvider } from '../../molecules/table/State';
import { GenericTabs } from '../../molecules/tabs/GenericTabs';
import { ITabOption } from '../../molecules/tabs/types';
import { RefreshButton } from '../../molecules/refreshButton';
import { AuditDashboardTable } from '../../organisms/dashboard/AuditDashboardTable';
import { DashboardMetrics } from './DashboardMetrics';
import { DashboardHeader } from './DashboardHeader';
import { dashboardMetricsMapper } from './dashboardMetricsMapper';
import { useDashboard } from './DashboardContext';
import { ColumnSettingsControl, ColumnVisibilityProvider } from '../../molecules/table/columnSettings';
import { AUDIT_DASHBOARD_DEFAULT_COLUMNS, AUDIT_DASHBOARD_STORAGE_KEY } from '../../organisms/dashboard/AuditDashboardTableConfig';
import { useTourContext } from 'src/presentations/guided-tours';
import NewFilterTickIcon from 'src/assets/images/icons/new-filter-tick.svg';
import NewCloseIcon from 'src/assets/images/icons/new-close.svg';
import { AuditFilterProvider, useAuditFilterContext } from '../../organisms/dashboard/context/AuditFilterContext';

const DashboardPageContent = () => {
  // Get data from Dashboard context
  const { statusStats, dataStats, filters, onStatusChange, onRefresh, onReset, isLoadingInvoiceData, isLoadingInvoiceStats } = useDashboard();
  const { resetAll } = useAuditFilterContext();

  // Transform status stats to filter nav options
  const statusNavOptions: ITabOption[] = useMemo(
    () => [
      {
        value: 'ACTION_NEEDED',
        label: 'Action Needed',
        count: statusStats?.actionNeeded || 0,
      },
      {
        value: 'ALL',
        label: 'All Exceptions',
        count: statusStats?.all || 0,
      },
    ],
    [statusStats]
  );

  // Transform metrics data for display
  // const metricsItems = useMemo(() => (dataStats ? dashboardMetricsMapper(dataStats) : []), [dataStats]);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    if (!filters.filters) return 0;

    let count = 0;

    // Helper to count non-empty arrays/values in an object, excluding specific fields
    const countInObject = (obj: Record<string, any>) => {
      Object.entries(obj).forEach(([key, value]) => {
        // Skip 'provider' and 'modality' fields
        if (key === 'serviceProviderCode' || key === 'modality') {
          return;
        }
        if (Array.isArray(value) && value.length > 0) {
          count++;
        } else if (value && !Array.isArray(value) && typeof value === 'object') {
          // recursive if needed, but for now we follow the simple logic
        } else if (value) {
          // Primitive values
        }
      });
    };

    // 1. Direct filters (if any top level in .filters)
    // 2. Nested filters
    const { dateFilters, tradeLaneFilters, shipmentFilters, ...rest } = filters.filters;

    // Count specific known groups
    if (dateFilters) countInObject(dateFilters);
    if (tradeLaneFilters) countInObject(tradeLaneFilters);
    if (shipmentFilters) countInObject(shipmentFilters);

    // Count any direct filters
    if (rest) countInObject(rest);

    return count;
  }, [filters.filters]);

  const handleClearFilters = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    onReset();
    resetAll();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClearFilters(e);
    }
  };

  const tour = useTourContext().appTour.registerTour('auditDashboard');

  return (
    <ColumnVisibilityProvider defaultColumns={AUDIT_DASHBOARD_DEFAULT_COLUMNS} storageKey={AUDIT_DASHBOARD_STORAGE_KEY}>
      <TableProvider>
        <LandingTopWidget />
        <LayoutSidebar>
          <div className='h-full flex flex-col min-h-0'>
            <div className='relative bg-neutral-10 h-full flex flex-col min-h-0'>
              {/* Dashboard Header with Filters */}
              <DashboardHeader tour={tour} />

              {/* Metrics Cards */}
              <DashboardMetrics
                dataStats={dataStats}
                isLoading={isLoadingInvoiceStats}
                activeFilterCount={activeFilterCount}
                id={tour.registerStep('metrics')}
              />

              {/* Table Section */}
              <div className='w-full h-full flex flex-col min-h-0'>
                <div className='h-full flex flex-col min-h-0  shadow-[0_10px_24px_0_rgba(0,0,0,0.1)]'>
                  {/* Status Tabs with Column Settings and Refresh Button */}
                  <div className='flex items-center justify-between gap-32 px-12 py-8 bg-white'>
                    <div className='flex items-center gap-12'>
                      <GenericTabs
                        options={statusNavOptions}
                        onChange={onStatusChange}
                        defaultSelected={filters.invoiceStatusCategory}
                        isLoading={isLoadingInvoiceData}
                        id={tour.registerStep('tabs')}
                      />

                      {/* Filter Counter Badge */}
                      {activeFilterCount > 0 && (
                        <div
                          role='button'
                          tabIndex={0}
                          onClick={handleClearFilters}
                          onKeyDown={handleKeyDown}
                          className='flex items-center justify-center gap-8 py-7 px-10 rounded-[50px] cursor-pointer hover:bg-neutral-30 transition-colors'
                          style={{ backgroundColor: '#EDEEF2' }}
                          title='Clear all filters'
                        >
                          <img src={NewFilterTickIcon} alt='Filter' className='w-14 h-14' />
                          <span className='text-12 font-medium' style={{ color: '#808080' }}>
                            {activeFilterCount} {activeFilterCount === 1 ? 'Column' : 'Columns'} Filtered
                          </span>
                          <img src={NewCloseIcon} alt='Clear filters' className='w-14 h-14' />
                        </div>
                      )}
                    </div>
                    <div className='flex items-center gap-12'>
                      <ColumnSettingsControl id={tour.registerStep('columnSettings')} />
                      <RefreshButton onClick={onRefresh} id={tour.registerStep('refresh')} />
                    </div>
                  </div>
                  {/* Table */}
                  <AuditDashboardTable />
                </div>
              </div>
            </div>
          </div>
        </LayoutSidebar>
      </TableProvider>
    </ColumnVisibilityProvider>
  );
};

export const DashboardPage = () => {
  return (
    <AuditFilterProvider>
      <DashboardPageContent />
    </AuditFilterProvider>
  );
};
